import { useEffect, useState } from 'react';
import {
  ApplicationIcon,
  MenuInput,
  type MenuMultiOption,
  TabContent,
  Typography,
} from '@saviynt/design-system';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAlertBanner } from '../../../../shared/app/contexts/AlertBannerContext';
import { ALERT_BANNER_TYPES, TOKENS } from '../../constants';
import {
  useManagePageDispatch,
  useManagePageState,
} from '../../contexts/ManagePageContext';
import { useSaveAgent } from '../../hooks/useAgentManage';
import {
  capitalizeFirstLetter,
  formatDateWithTime,
  getPlatformLogo,
} from '../../utils/utilManagepage';
import { type AgentRow, LlmModelIcon, RowDash } from '../ManagePageTable';

import { getCriticalMenuOptions } from './SidePanelShared';

interface SidePanelOverviewTabProps {
  agent: AgentRow;
  activeTab: string;
}

/**
 * Overview tab for the agent side panel (tab value `"1"`).
 *
 * Renders a vertical card of label-value pairs for the selected agent:
 * Agent Name, Display Name, Agent ID, Status, Platform (with icon), LLM Model (with icon),
 * Description, Instruction Prompt, Criticality, Environment, Last Used on,
 * Last Updated, Last Updated by, Created on, and Created by.
 *
 * Date fields are formatted using {@link formatDateWithTime} with the
 * `globalDateFormat` from {@link useManagePageState}. Missing values render a {@link RowDash}.
 * Status is displayed with {@link capitalizeFirstLetter} formatting.
 *
 * Criticality is editable via a {@link MenuInput} dropdown built from
 * {@link getCriticalMenuOptions}. Selecting a new value triggers {@link useSaveAgent};
 * success/error feedback is surfaced through the shared alert banner.
 *
 * @param props.agent - The {@link AgentRow} to display.
 * @param props.activeTab - Currently active tab value for {@link TabContent} visibility control.
 *
 * @returns Tab content with the agent overview card and inline criticality editing.
 */
export default function SidePanelOverviewTab({
  agent,
  activeTab,
}: SidePanelOverviewTabProps) {
  // Hooks
  const intl = useIntl();
  const dispatch = useManagePageDispatch();
  const { show: showAlertBanner } = useAlertBanner();
  const { globalDateFormat } = useManagePageState();

  // States
  const [criticalityOptions, setCriticalityOptions] = useState<
    MenuMultiOption[]
  >([]);

  // Hooks
  const { mutate: saveAgent, isPending } = useSaveAgent({
    onSuccess: (criticality) => {
      dispatch({
        refreshAgent: { [agent.agentId]: { criticality } },
      });
      showAlertBanner({
        colorTheme: ALERT_BANNER_TYPES.SUCCESS,
        title: intl.formatMessage({
          id: 'yg2HFT',
          defaultMessage: 'Success',
          description: 'Success',
        }),
        description: intl.formatMessage({
          id: 'yS7dwz',
          defaultMessage: 'Criticality has been updated successfully.',
          description: 'Criticality has been updated successfully.',
        }),
      });
    },
    onError: (errorMessage) => {
      setCriticalityOptions((pcs) =>
        pcs.map((cps) => ({
          ...cps,
          isChecked: agent.criticality?.toLowerCase().includes(cps.value),
        }))
      );

      showAlertBanner({
        colorTheme: ALERT_BANNER_TYPES.CRITICAL,
        title: intl.formatMessage({
          id: 'KFYqlI',
          defaultMessage: 'Failed',
          description: 'Failed',
        }),
        description: errorMessage,
      });
    },
  });

  // Effects
  useEffect(() => {
    setCriticalityOptions(
      getCriticalMenuOptions(intl).map((cps) => ({
        ...cps,
        isChecked: agent.criticality?.toLowerCase().includes(cps.value),
      }))
    );
  }, [agent?.agentId]);

  return (
    <TabContent className='SidePanelOverviewTab' value={activeTab} tabValue='1'>
      <div className='SidePanelOverviewTab-cardWrapper'>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='pMn94H'
              defaultMessage='Agent Name'
              description='Agent Name'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
            {agent.name}
          </Typography>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='pkPc+V'
              defaultMessage='Display Name'
              description='Display Name'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
            {agent.displayName ?? <RowDash />}
          </Typography>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='PXxKPw'
              defaultMessage='Agent ID'
              description='Agent ID'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
            {agent.agentId}
          </Typography>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='w/rzfS'
              defaultMessage='Status'
              description='Status'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
            {agent.status ? capitalizeFirstLetter(agent.status) : <RowDash />}
          </Typography>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='hyBCHI'
              defaultMessage='Platform'
              description='Platform'
            />
          </Typography>
          <div className='SidePanelOverviewTab-cardLineItemContent'>
            {agent.platform !== null ? (
              <ApplicationIcon
                logo={getPlatformLogo(agent.platform)}
                size='xSmall'
              />
            ) : null}
            <Typography
              kind='body2'
              lineLimit={2}
              tooltipConfigs={{ isPortalEnabled: true }}
              color={TOKENS.CF_SEC_BLD}>
              {agent.platform ?? <RowDash />}
            </Typography>
          </div>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='alU337'
              defaultMessage='LLM Model'
              description='LLM Model'
            />
          </Typography>
          <div className='SidePanelOverviewTab-cardLineItemContent'>
            {agent.agentMetadata?.model ? (
              <LlmModelIcon model={agent.agentMetadata?.model} />
            ) : null}
            <Typography
              kind='body2'
              lineLimit={1}
              tooltipConfigs={{ isPortalEnabled: true }}
              color={TOKENS.CF_SEC_BLD}>
              {agent.agentMetadata?.model ?? <RowDash />}
            </Typography>
          </div>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='3T09+Q'
              defaultMessage='Description'
              description='Description'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD} lineLimit={2}>
            {agent.description ?? <RowDash />}
          </Typography>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='gKreMb'
              defaultMessage='Instruction Prompt'
              description='Instruction Prompt'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD} lineLimit={2}>
            {agent.agentMetadata?.instructionPrompt ?? <RowDash />}
          </Typography>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='w4HbiC'
              defaultMessage='Criticality'
              description='Criticality'
            />
          </Typography>
          <div className='SidePanelOverviewTab-cardLineItemContent'>
            <MenuInput
              {...({ isNodeList: true } as Record<string, unknown>)}
              placeholder={intl.formatMessage({
                id: 'QH7rUM',
                defaultMessage: 'Select Criticality',
                description: 'Select Criticality',
              })}
              isDisabled={isPending}
              options={criticalityOptions}
              onChange={(so) => {
                setCriticalityOptions((pcs) =>
                  pcs.map((c) => ({ ...c, isChecked: c.value === so.value }))
                );

                saveAgent({
                  agentId: agent.agentId,
                  criticality: so.value,
                });
              }}
              className='SidePanelOverviewTab-criticalityMenu'
            />
          </div>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='0AnPQt'
              defaultMessage='Environment'
              description='Environment'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
            {agent.environment ?? <RowDash />}
          </Typography>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='zpDHBR'
              defaultMessage='Last Used on'
              description='Last Used on'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
            {formatDateWithTime(agent.lastUsedAt, globalDateFormat) ?? (
              <RowDash />
            )}
          </Typography>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='Pzb4Cj'
              defaultMessage='Last Updated'
              description='Last Updated'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
            {formatDateWithTime(agent.updatedAt, globalDateFormat) ?? (
              <RowDash />
            )}
          </Typography>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='ETTpMY'
              defaultMessage='Last Updated by'
              description='Last Updated by'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
            {formatDateWithTime(agent.updatedBy, globalDateFormat) ?? (
              <RowDash />
            )}
          </Typography>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='8ysZIx'
              defaultMessage='Created on'
              description='Created on'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
            {formatDateWithTime(agent.createdAt, globalDateFormat) ?? (
              <RowDash />
            )}
          </Typography>
        </div>
        <div className='SidePanelOverviewTab-cardLineItem'>
          <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='2XFOYa'
              defaultMessage='Created by'
              description='Created by'
            />
          </Typography>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
            {formatDateWithTime(agent.createdBy, globalDateFormat) ?? (
              <RowDash />
            )}
          </Typography>
        </div>
      </div>
    </TabContent>
  );
}
